/** Copy and assets for the home page's "Prompt — try it" panel. */

export const PROMPT_COMPOSER_PLACEHOLDER = 'Describe the email you want to send'

export type SceneEmail = {
  src: string
  /** Unique within its scene (it also keys the card). */
  alt: string
}

export type PromptScene = {
  /** Stable id; the panel's `data-scene` names the scene on screen. */
  id: 'welcome' | 'launch' | 'edit' | 'remix' | 'preview'
  /** What the composer types. */
  prompt: string
  /** The emails whose tops rise under the composer once it is typed. */
  emails: ReadonlyArray<SceneEmail>
}

const SCENE = (name: string) => `/landing/home/prompt-scenes/${name}.webp`

/** Every scene email is a 520×800 capture, shown top-cropped. */
export const SCENE_EMAIL_SIZE = { width: 520, height: 800 } as const

/**
 * The prompts the composer types and erases in turn — one per thing Brew
 * does with an email — each answered by real emails from production
 * (captured 2026-10-01): Gamma's published welcome flow, the launch
 * variants and the welcome edit Brew's own team made for Brew, a
 * MasterClass gallery template remixed into the Tech@NYU brand, and The
 * Monthly Brew #1 as Gmail, Outlook and Apple Mail (iOS) really rendered it.
 */
export const PROMPT_SCENES: ReadonlyArray<PromptScene> = [
  {
    id: 'welcome',
    prompt: 'Create a welcome series for new signups',
    emails: [
      { src: SCENE('gamma-welcome'), alt: 'Gamma welcome email, day 0' },
      { src: SCENE('gamma-prompt'), alt: 'Gamma prompts email, day 2' },
      { src: SCENE('gamma-steps'), alt: 'Gamma getting-started email, day 3' },
      { src: SCENE('gamma-refine'), alt: 'Gamma next-steps email, day 4' },
    ],
  },
  {
    id: 'launch',
    prompt: "We're announcing a new product launch, create 3 variants",
    emails: [
      { src: SCENE('brew-slack-bold'), alt: 'Slack launch, bold variant' },
      {
        src: SCENE('brew-slack-walkthrough'),
        alt: 'Slack launch, walkthrough variant',
      },
      { src: SCENE('brew-slack-launch'), alt: 'Slack launch, product variant' },
    ],
  },
  {
    id: 'edit',
    prompt: 'Make the headline sharper and the first steps quicker to scan',
    emails: [
      { src: SCENE('brew-welcome-before'), alt: 'The welcome email before' },
      { src: SCENE('brew-welcome-after'), alt: 'The edited welcome email' },
    ],
  },
  {
    id: 'remix',
    prompt: 'Remix this template in our brand',
    emails: [
      {
        src: SCENE('masterclass-original'),
        alt: 'The MasterClass template',
      },
      { src: SCENE('techatnyu-remix'), alt: 'The remix in the Tech@NYU brand' },
    ],
  },
  {
    id: 'preview',
    prompt: 'Preview it in Gmail, Outlook, and Apple Mail',
    emails: [
      { src: SCENE('preview-gmail'), alt: 'The email in Gmail' },
      { src: SCENE('preview-outlook'), alt: 'The email in Outlook' },
      { src: SCENE('preview-apple-mail'), alt: 'The email in Apple Mail' },
    ],
  },
]

/** The composer opens with the first scene's prompt already typed. */
export const PROMPT_COMPOSER_EXAMPLE = PROMPT_SCENES[0]?.prompt ?? ''
